import { action, computed, makeObservable, observable } from 'mobx';
import type { CreateTarget } from 'actions';
import type { EditableEntity } from 'edit/EditableEntity';
import type { FolderReference } from 'edit/reference/EditFolderReference';
import type { ZoneReference } from 'edit/reference/EditZoneReference';
import type { Lock } from 'api/locks/endpoints/getLock';
import type { SaveLock } from 'api/locks/endpoints/postLock';

export interface EditableLockOptions {
  /** Workspace create pre-target: seeds the zone + folder of a blank create form. */
  readonly createIn?: CreateTarget;
}

/**
 * A lock open for editing. A flat, single-record form — no aggregate children —
 * built directly from the loaded wire record (or `null` for a blank create
 * form). The model *is* the editable state; `toSave` projects straight back to
 * the request body.
 *
 * `original` is the pre-edit identity (`zone_name | name`) the PUT addresses and
 * the POST-vs-PUT branch keys on; `null` means a not-yet-saved record. Written
 * only internally (via `_init`), so it is exposed read-only, and flips to a real
 * identity after the first successful save (via `reinitialize`).
 *
 * `isDirty` is "create is always dirty, otherwise the current `toSave` body
 * differs from the snapshot captured at load / reinitialize" — a structural
 * comparison that reads clean again if the user reverts an edit by hand.
 */
export class EditableLock implements EditableEntity<Lock | null> {
  description: string | null = null;
  folder: FolderReference | null = null;
  limit: number | null = null;
  name: string | null = null;
  private _original: { zoneName: string; name: string } | null = null;
  private _snapshot = '';
  zone: ZoneReference | null = null;

  constructor(loaded: Lock | null, options?: EditableLockOptions) {
    makeObservable<EditableLock, '_original'>(this, {
      description: observable,
      folder: observable.ref,
      limit: observable,
      name: observable,
      _original: observable.ref,
      zone: observable.ref,
      isDirty: computed,
    });
    this._init(loaded, options?.createIn);
  }

  get isCreating(): boolean {
    return this._original === null;
  }

  get isDirty(): boolean {
    return this._original === null || JSON.stringify(this.toSave()) !== this._snapshot;
  }

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this
   * to choose create vs update and to build the PUT URL.
   */
  get original(): { zoneName: string; name: string } | null {
    return this._original;
  }

  /**
   * Reset to the loaded record (or a blank create form when `null`) and
   * recapture the dirty baseline. Called after a successful load or save.
   */
  reinitialize = (loaded: Lock | null): void => {
    this._init(loaded);
  };

  toSave(): SaveLock {
    return {
      zone_name: this.zone?.name ?? null,
      name: this.name,
      limit: this.limit,
      description: this.description,
      folder_id: this.folder?.folder_id ?? null,
    };
  }

  // Shared by the constructor and `reinitialize`. Wrapped in `action` so the
  // whole reset lands as one transaction — observers see only the final state,
  // never a half-applied intermediate, and re-render once rather than per write.
  //
  // A loaded record's `folder_path` is not carried (the single-GET omits it):
  // the folder is seeded with an empty display path and EditFolderReference
  // re-resolves the label from its loaded options, keyed on `folder_id`.
  private readonly _init = action((loaded: Lock | null, createIn?: CreateTarget): void => {
    if (loaded !== null) {
      this._original = { zoneName: loaded.zone_name, name: loaded.name };
      this.name = loaded.name;
      this.zone = { name: loaded.zone_name };
      this.folder = loaded.folder_id !== null ? { folder_id: loaded.folder_id, path: '' } : null;
      this.description = loaded.description;
      this.limit = loaded.limit;
    } else {
      this._original = null;
      this.name = null;
      // Seed the zone/folder from the workspace create pre-target when present.
      this.zone = createIn ? { name: createIn.zoneName } : null;
      this.folder = createIn?.folderId != null ? { folder_id: createIn.folderId, path: '' } : null;
      this.description = null;
      this.limit = null;
    }
    this._snapshot = JSON.stringify(this.toSave());
  });
}
